// apps/admin/src/stores/system-events.store.ts
//
// Phase 8.3 Wave 5 Task 65 — system events Zustand store.
//
// Why a dedicated store on top of `useWsConnectionStore`:
//   - The connection store is a generic ring buffer for any channel
//     mirrored at the page level. The SA-only `system.*` event stream
//     needs typed shape, severity-aware badge counters, and a small per-
//     event-kind cache so the AppShell's banner can render "Critical
//     health alert — 2 active" without re-subscribing per render.
//   - Per CONTEXT the WS schema for `system.*` is fixed and
//     SA-only; only SA-scoped accounts subscribe. The store
//     keeps the per-kind counters separate from the notification bell
//     (useNotificationStore, Phase 8.1 Task 03) so a future un-collapse
//     to a dedicated SA banner is a one-prop change.
//
// vg-binding: CONTEXT:decisions=P8.D-212,P8.D-260
// vg-binding: INTERFACE-STANDARDS:error-shape

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Canonical SA-only event kinds (per CONTEXT). The runtime
 * channel only emits these — anything else is dropped at the BE WS
 * gateway. Keep this enum aligned with the BE-side `system.*` schema.
 */
export type SystemEventKind =
  | 'system.health.alert'
  | 'system.maintenance.changed'
  | 'system.emergency.changed'
  | 'system.announcement.new'
  | 'approval.expired';

export type SystemEventSeverity = 'info' | 'warning' | 'critical';

export interface SystemEvent {
  /** Stable event ID — used to dedupe on reconnect resume. */
  id: string;
  kind: SystemEventKind;
  severity: SystemEventSeverity;
  /** Short summary — surfaced inside the toast title. */
  title: string;
  /** Optional human-readable description — toast body. */
  description?: string;
  /** Optional deep-link href (e.g. `/admin/health-alerts/:id`). */
  href?: string;
  /** ISO timestamp from BE; falls back to client recv time. */
  receivedAt: string;
  /** Raw payload from the WS frame — preserved for diagnostics. */
  payload?: unknown;
}

/** Per-event-kind badge counter (driving SA banner + sidebar pip). */
export type SystemEventCounters = Record<SystemEventKind, number>;

export interface SystemEventStoreState {
  /** Bounded ring buffer of recent events (most-recent first). */
  recent: SystemEvent[];
  /** Per-kind active counter — banner reads from here. */
  counters: SystemEventCounters;
  /** Last event id seen — used to resume after reconnect. */
  lastEventId: string | null;
  /** Total events received since boot — UAT visibility. */
  totalReceived: number;

  // ---- Mutations --------------------------------------------------------

  /** Push a new event. Bumps counter + lastEventId; trims buffer. */
  pushEvent: (event: SystemEvent) => void;
  /** Clear counter for a kind (e.g. after user reads a banner). */
  clearCounter: (kind: SystemEventKind) => void;
  /** Reset all state — called by useAuthStore.logout(). */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const RECENT_CAP = 50;

const ZERO_COUNTERS: SystemEventCounters = {
  'system.health.alert': 0,
  'system.maintenance.changed': 0,
  'system.emergency.changed': 0,
  'system.announcement.new': 0,
  'approval.expired': 0,
};

function freshCounters(): SystemEventCounters {
  return { ...ZERO_COUNTERS };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSystemEventStore = create<SystemEventStoreState>()(
  subscribeWithSelector((set, get) => ({
    recent: [],
    counters: freshCounters(),
    lastEventId: null,
    totalReceived: 0,

    pushEvent(event) {
      // Dedupe by id — reconnect-resume may replay the most recent batch.
      const cur = get();
      if (cur.recent.some((e) => e.id === event.id)) return;

      const nextRecent =
        cur.recent.length >= RECENT_CAP
          ? [event, ...cur.recent.slice(0, RECENT_CAP - 1)]
          : [event, ...cur.recent];

      const nextCounters: SystemEventCounters = {
        ...cur.counters,
        [event.kind]: (cur.counters[event.kind] ?? 0) + 1,
      };

      set({
        recent: nextRecent,
        counters: nextCounters,
        lastEventId: event.id,
        totalReceived: cur.totalReceived + 1,
      });
    },

    clearCounter(kind) {
      set({
        counters: {
          ...get().counters,
          [kind]: 0,
        },
      });
    },

    reset() {
      set({
        recent: [],
        counters: freshCounters(),
        lastEventId: null,
        totalReceived: 0,
      });
    },
  })),
);

export default useSystemEventStore;
